import type ChargePriceListItemResponse from "entities/chargePriceListItemResponse";
import type ChargePriceListResponse from "entities/chargePriceListResponse";
import ChargePriceListsResponse from "entities/chargePriceListsResponse";
import { action, computed, observable, when } from "mobx";
import type ChargesRepository from "repositories/chargesRepository";
import type SecurityService from "./securityService";

export default class PriceListService {
  @observable private _chargePriceListItems: ChargePriceListItemResponse[];
  @observable.shallow private _chargeTypePriceLists: ChargePriceListResponse[] = [];
  @observable isInitialized = false;
  @observable initializationInProgress = false;
  @observable isChargeTypePriceListsInitialized = false;
  @observable chargeTypePriceListsInitializationInProgress = false;

  constructor(private repository: ChargesRepository, private securityService: SecurityService) {}

  /**
   * Price list items for the simple mapping of `priceListItemId` to the charge type, the fuel
   * type and the price. Loaded once on application start and used across the whole application.
   */
  @action.bound
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    if (this.initializationInProgress) {
      await when(() => !this.initializationInProgress);
      return;
    }

    this.initializationInProgress = true;

    try {
      const priceLists = await this.repository.chargePriceLists();

      if (priceLists instanceof ChargePriceListsResponse) {
        this._chargePriceListItems = priceLists.items.flatMap(x => x.items.flatMap(y => y));
        this.isInitialized = true;
      } else {
        this._chargePriceListItems = [];
        this.isInitialized = false;
      }
    } finally {
      // Without this, a failed request would leave the flag set and every later caller
      // would wait on `when()` forever.
      this.initializationInProgress = false;
    }
  }

  /**
   * Whole price lists including their validity - needed to build the charge type overview of
   * the charge types administration module. Loaded only when that module is opened.
   */
  @action.bound
  async initializeChargeTypePriceLists() {
    if (this.isChargeTypePriceListsInitialized) {
      return;
    }

    if (this.chargeTypePriceListsInitializationInProgress) {
      await when(() => !this.chargeTypePriceListsInitializationInProgress);
      return;
    }

    this.chargeTypePriceListsInitializationInProgress = true;

    try {
      const priceLists = await this.repository.chargeTypePriceLists();

      if (priceLists instanceof ChargePriceListsResponse) {
        this._chargeTypePriceLists = priceLists.items;
        this.isChargeTypePriceListsInitialized = true;
      } else {
        this._chargeTypePriceLists = [];
        this.isChargeTypePriceListsInitialized = false;
      }
    } finally {
      this.chargeTypePriceListsInitializationInProgress = false;
    }
  }

  @computed
  get chargePriceList() {
    return this._chargePriceListItems ?? [];
  }

  /** Whole price lists including their validity - needed to build the charge type overview. */
  @computed
  get chargePriceLists() {
    return this._chargeTypePriceLists ?? [];
  }

  @action.bound
  async startInitialization() {
    await this.initialize();
  }

  @action.bound
  async startChargeTypePriceListsInitialization() {
    await this.initializeChargeTypePriceLists();
  }

  @action.bound
  priceListItemById(priceListId: string): ChargePriceListItemResponse | undefined {
    return this._chargePriceListItems?.find(x => x.id === priceListId);
  }

  @action.bound
  priceListItemByFuelTypeId(priceListId: string, fuelTypeId: string): ChargePriceListItemResponse | undefined {
    return this._chargePriceListItems?.find(x => x.id === priceListId && x.fuelTypeId === fuelTypeId);
  }

  @action.bound
  priceListItemByChargeTypeId(priceListId: string, chargeTypeId: string): ChargePriceListItemResponse | undefined {
    return this._chargePriceListItems?.find(x => x.id === priceListId && x.chargeTypeId === chargeTypeId);
  }
}
